import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { IRecordType } from 'types/recordType';
import { IRecordHeaders } from 'ui-component/records/types';
import { RecordViewDialog } from 'ui-component/RecordView';
import { RecordViewButtonMenu } from './components';
import { openInThisTabRecordViewAction, openInANewTabRecordViewAction } from './utils';
import { useLocalStorageListener, RECORD_VIEW_PREF } from 'hooks/useLocalStorageListener';

const MENU_PREF_KEY = 'recordViewMenuPref';

export type RecordViewButtonProps = {
    recordId: string;
    recordType: IRecordType;
    onUpdate: (updatedRecord: IRecordHeaders) => void;
};

export const RecordViewButton = ({ recordId, recordType, onUpdate }: RecordViewButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const selectedPref = useLocalStorageListener(MENU_PREF_KEY, 'OPEN_IN_THIS_TAB', Object.values(RECORD_VIEW_PREF));
    const [openDialog, setOpenDialog] = useState(false);

    // Handlers for each option
    const handleOpenInThisTab = () => {
        handleMenuClose();
        openInThisTabRecordViewAction(recordId, recordType);
    };
    const handleOpenInNewTab = () => {
        handleMenuClose();
        openInANewTabRecordViewAction(recordId, recordType);
    };
    const handleOpenInPopup = () => {
        handleMenuClose();
        setOpenDialog(true);
    };

    // Left click: execute the selected preference
    const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (event.type === 'click' && event.button === 0) {
            const selected = menuOptions.find((opt) => opt.value === selectedPref);
            if (selected) selected.handler();
        }
    };
    // Right click: open menu
    const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Menu options (inside component, not global const)
    const menuOptions = [
        { label: 'Open in this tab', value: RECORD_VIEW_PREF.OPEN_IN_THIS_TAB, handler: handleOpenInThisTab },
        { label: 'Open in a new tab', value: RECORD_VIEW_PREF.OPEN_IN_A_NEW_TAB, handler: handleOpenInNewTab },
        { label: 'Open in a pop-up window', value: RECORD_VIEW_PREF.OPEN_IN_A_POP_UP_WINDOW, handler: handleOpenInPopup }
    ];

    return (
        <>
            <IconButton size="small" onClick={handleButtonClick} onContextMenu={handleContextMenu} sx={{ ml: 0.5 }}>
                <Visibility fontSize="small" />
            </IconButton>
            <RecordViewButtonMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                recordId={recordId}
                recordType={recordType}
                onOpenInAPopUp={() => setOpenDialog(true)}
                onDefaultPrefChange={(pref) => {
                    // Dispatch custom event to notify the change
                    window.dispatchEvent(
                        new CustomEvent('localStorageChange', {
                            detail: { key: MENU_PREF_KEY, value: pref }
                        })
                    );
                }}
            />
            {openDialog && (
                <RecordViewDialog
                    recordData={{ id: recordId, recordType }}
                    open={openDialog}
                    onClose={(success?: boolean, recordData?: IRecordHeaders) => {
                        setOpenDialog(false);
                        if (success && recordData) onUpdate(recordData);
                    }}
                />
            )}
        </>
    );
};
